import { z } from "zod";
import { localDateSchema, timestampSchema, uuidSchema } from "../common/primitives.js";
import { assetKindSchema } from "../maintenance/api.js";
import { reportFormatSchema, reportFrequencySchema, reportKeySchema, } from "./common.js";
/**
 * Rapor altyapısı sözleşmeleri (Prompt 22, ADR 0022 §12–16): rapor parametreleri, dışa aktarım, kaydedilmiş filtre, favori,
 * rapor ayarları (logo) ve zamanlanmış raporlar.
 */
// ── Rapor parametreleri (kaydedilmiş filtre + zamanlama `params`'ı) ──────────
/** Web'in kaydedilmiş filtrede tuttuğu dönem hazır ayarı; zamanlamada kullanılmaz (dönem sıklıktan gelir). */
export const REPORT_RANGE_PRESETS = [
    "TODAY",
    "YESTERDAY",
    "LAST_7_DAYS",
    "LAST_30_DAYS",
    "THIS_MONTH",
    "LAST_MONTH",
];
export const reportRangePresetSchema = z.enum(REPORT_RANGE_PRESETS);
export const REPORT_RANGE_PRESET_LABELS = {
    TODAY: "Bugün",
    YESTERDAY: "Dün",
    LAST_7_DAYS: "Son 7 gün",
    LAST_30_DAYS: "Son 30 gün",
    THIS_MONTH: "Bu ay",
    LAST_MONTH: "Geçen ay",
};
const scopeShape = {
    companyId: uuidSchema.optional(),
    stationIds: z.array(uuidSchema).max(200).default([]),
    rangePreset: reportRangePresetSchema.optional(),
};
export const PROFITABILITY_GROUPINGS = ["STATION", "BUSINESS_UNIT", "STATION_BUSINESS_UNIT"];
export const reportParamsSchemas = {
    DAILY_SALES: z.object(scopeShape).strict(),
    STATION_COMPARISON: z.object(scopeShape).strict(),
    FUEL_ANALYTICS: z
        .object({
        companyId: uuidSchema.optional(),
        stationId: uuidSchema.optional(),
        windowDays: z.number().int().min(1).max(14).default(3),
        rangePreset: reportRangePresetSchema.optional(),
    })
        .strict(),
    PROFITABILITY: z
        .object({
        companyId: uuidSchema,
        groupBy: z.enum(PROFITABILITY_GROUPINGS).default("STATION_BUSINESS_UNIT"),
        includeDrafts: z.boolean().default(false),
        rangePreset: reportRangePresetSchema.optional(),
    })
        .strict(),
    FINANCE_OVERVIEW: z.object({ companyId: uuidSchema.optional() }).strict(),
    MAINTENANCE: z
        .object({
        companyId: uuidSchema.optional(),
        stationId: uuidSchema.optional(),
        assetKind: assetKindSchema.optional(),
        rangePreset: reportRangePresetSchema.optional(),
    })
        .strict(),
};
/** Anahtara göre parametre doğrulama (bilinmeyen alan reddedilir). */
export function parseReportParams(key, value) {
    return reportParamsSchemas[key].parse(value);
}
const paramsRecordSchema = z.record(z.string(), z.unknown());
/** Parametre kaydı rapor anahtarına göre doğrulanır; hata alanı `params`'a düşer. */
function refineParams(value, context) {
    const result = reportParamsSchemas[value.reportKey].safeParse(value.params);
    if (!result.success)
        for (const issue of result.error.issues)
            context.addIssue({
                code: "custom",
                message: issue.message,
                path: ["params", ...issue.path.map(String)],
            });
}
// ── Dışa aktarım ─────────────────────────────────────────────────────────────
export const reportExportParamsSchema = z.object({ reportKey: reportKeySchema });
/** `GET /reports/{reportKey}/export` — rapor parametreleri düz sorgu alanlarıdır (anahtara göre doğrulanır). */
export const reportExportQuerySchema = z
    .object({
    format: reportFormatSchema,
    from: localDateSchema.optional(),
    to: localDateSchema.optional(),
    companyId: uuidSchema.optional(),
    stationId: uuidSchema.optional(),
    stationIds: z
        .string()
        .optional()
        .transform((value) => (value ? value.split(",").filter(Boolean) : []))
        .pipe(z.array(uuidSchema).max(200)),
    windowDays: z.coerce.number().int().min(1).max(14).optional(),
    groupBy: z.enum(PROFITABILITY_GROUPINGS).optional(),
    includeDrafts: z.enum(["true", "false"]).optional(),
    assetKind: assetKindSchema.optional(),
})
    .refine((value) => !value.from || !value.to || value.from <= value.to, {
    message: "Başlangıç tarihi bitişten sonra olamaz",
    path: ["from"],
});
export const REPORT_CONTENT_TYPES = {
    XLSX: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    PDF: "application/pdf",
};
// ── Rapor kataloğu (merkez sayfası) ──────────────────────────────────────────
export const reportCatalogItemSchema = z.object({
    key: reportKeySchema,
    label: z.string(),
    description: z.string(),
    /** Kullanıcının bu raporu görme yetkisi var mı (en az bir bölüm). */
    available: z.boolean(),
    favorite: z.boolean(),
    /** Dönem gerektirir mi (finans özeti anlık durumdur). */
    periodic: z.boolean(),
});
export const reportCatalogSchema = z.object({
    items: z.array(reportCatalogItemSchema),
    canSchedule: z.boolean(),
    canManageSettings: z.boolean(),
});
// ── Kaydedilmiş filtreler ────────────────────────────────────────────────────
const filterNameSchema = z.string().trim().min(1, "Ad zorunludur").max(80, "Ad en çok 80 karakter olabilir");
export const createSavedFilterSchema = z
    .object({
    reportKey: reportKeySchema,
    name: filterNameSchema,
    params: paramsRecordSchema,
    isDefault: z.boolean().default(false),
})
    .superRefine(refineParams);
export const updateSavedFilterSchema = z.object({
    name: filterNameSchema.optional(),
    params: paramsRecordSchema.optional(),
    isDefault: z.boolean().optional(),
    version: z.number().int().positive(),
});
export const savedFilterSchema = z.object({
    id: uuidSchema,
    reportKey: reportKeySchema,
    name: z.string(),
    params: paramsRecordSchema,
    isDefault: z.boolean(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    version: z.number().int(),
});
export const savedFilterListQuerySchema = z.object({ reportKey: reportKeySchema.optional() });
export const savedFilterListSchema = z.object({ items: z.array(savedFilterSchema) });
export const savedFilterParamsSchema = z.object({ id: uuidSchema });
// ── Favoriler ────────────────────────────────────────────────────────────────
export const reportFavoriteParamsSchema = z.object({ reportKey: reportKeySchema });
export const reportFavoritesSchema = z.object({
    items: z.array(z.object({ reportKey: reportKeySchema, createdAt: timestampSchema })),
});
// ── Rapor ayarları (PDF logosu) ──────────────────────────────────────────────
export const reportSettingsSchema = z.object({
    logoFileId: uuidSchema.nullable(),
    /** Kısa ömürlü imzalı indirme adresi (önizleme). */
    logoUrl: z.string().nullable(),
    footerNote: z.string().nullable(),
    updatedAt: timestampSchema.nullable(),
    /** Hiç kaydedilmediyse 0. */
    version: z.number().int().nonnegative(),
});
export const saveReportSettingsSchema = z.object({
    logoFileId: uuidSchema.nullable(),
    footerNote: z.string().trim().min(1).max(200).nullable(),
    version: z.number().int().nonnegative(),
});
// ── Zamanlanmış raporlar ─────────────────────────────────────────────────────
export const REPORT_SCHEDULE_MAX_RECIPIENTS = 20;
export const REPORT_RUN_STATUSES = ["SENT", "PARTIAL", "FAILED", "SKIPPED"];
export const reportRunStatusSchema = z.enum(REPORT_RUN_STATUSES);
export const REPORT_RUN_STATUS_LABELS = {
    SENT: "Gönderildi",
    PARTIAL: "Kısmen gönderildi",
    FAILED: "Başarısız",
    SKIPPED: "Atlandı",
};
export const REPORT_RECIPIENT_STATUSES = ["SENT", "SKIPPED", "FAILED"];
export const reportRecipientStatusSchema = z.enum(REPORT_RECIPIENT_STATUSES);
export const REPORT_RUN_TRIGGERS = ["SCHEDULE", "MANUAL"];
export const reportRunTriggerSchema = z.enum(REPORT_RUN_TRIGGERS);
const timeOfDaySchema = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Saat SS:DD biçiminde olmalı (ör. 07:30)");
const scheduleShape = {
    name: z.string().trim().min(1, "Ad zorunludur").max(120),
    reportKey: reportKeySchema,
    params: paramsRecordSchema,
    format: reportFormatSchema,
    frequency: reportFrequencySchema,
    timeOfDay: timeOfDaySchema,
    weekday: z.number().int().min(1).max(7).nullable(),
    monthDay: z.number().int().min(1).max(28).nullable(),
    recipientUserIds: z
        .array(uuidSchema)
        .min(1, "En az bir alıcı seçin")
        .max(REPORT_SCHEDULE_MAX_RECIPIENTS)
        .refine((ids) => new Set(ids).size === ids.length, "Aynı alıcı iki kez seçilemez"),
};
function refineTiming(value, context) {
    if (value.frequency === "WEEKLY" && value.weekday === null)
        context.addIssue({
            code: "custom",
            message: "Haftalık raporda haftanın günü seçilmeli",
            path: ["weekday"],
        });
    if (value.frequency !== "WEEKLY" && value.weekday !== null)
        context.addIssue({
            code: "custom",
            message: "Haftanın günü yalnızca haftalık raporda seçilir",
            path: ["weekday"],
        });
    if (value.frequency === "MONTHLY" && value.monthDay === null)
        context.addIssue({ code: "custom", message: "Aylık raporda ayın günü seçilmeli", path: ["monthDay"] });
    if (value.frequency !== "MONTHLY" && value.monthDay !== null)
        context.addIssue({
            code: "custom",
            message: "Ayın günü yalnızca aylık raporda seçilir",
            path: ["monthDay"],
        });
}
export const createReportScheduleSchema = z.object(scheduleShape).superRefine((value, context) => {
    refineTiming(value, context);
    refineParams(value, context);
});
export const replaceReportScheduleSchema = z
    .object({ ...scheduleShape, version: z.number().int().positive() })
    .superRefine((value, context) => {
    refineTiming(value, context);
    refineParams(value, context);
});
export const reportScheduleActionSchema = z.object({ version: z.number().int().positive() });
export const reportScheduleParamsSchema = z.object({ id: uuidSchema });
const personSchema = z.object({ userId: uuidSchema, fullName: z.string() });
export const reportScheduleSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    reportKey: reportKeySchema,
    params: paramsRecordSchema,
    format: reportFormatSchema,
    frequency: reportFrequencySchema,
    timeOfDay: z.string(),
    weekday: z.number().int().nullable(),
    monthDay: z.number().int().nullable(),
    owner: personSchema,
    recipients: z.array(personSchema),
    isActive: z.boolean(),
    pausedReason: z.string().nullable(),
    nextRunAt: timestampSchema.nullable(),
    lastRunAt: timestampSchema.nullable(),
    lastRunStatus: reportRunStatusSchema.nullable(),
    /** Oturumdaki kullanıcı düzenleyebilir mi (sahip ya da zamanlama yöneticisi). */
    canEdit: z.boolean(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    version: z.number().int(),
});
export const reportScheduleListSchema = z.object({ items: z.array(reportScheduleSchema) });
export const reportScheduleRunSchema = z.object({
    id: uuidSchema,
    trigger: reportRunTriggerSchema,
    periodFrom: localDateSchema,
    periodTo: localDateSchema,
    format: reportFormatSchema,
    status: reportRunStatusSchema,
    recipients: z.array(personSchema.extend({
        status: reportRecipientStatusSchema,
        reason: z.string().nullable(),
        byteSize: z.number().int().nullable(),
    })),
    error: z.string().nullable(),
    startedAt: timestampSchema,
    finishedAt: timestampSchema,
});
export const reportScheduleRunListSchema = z.object({ items: z.array(reportScheduleRunSchema) });
/** Zamanlamaya alıcı olarak eklenebilecek kiracı üyeleri (etkin). */
export const reportRecipientCandidatesSchema = z.object({ items: z.array(personSchema) });
//# sourceMappingURL=infrastructure.js.map